import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory, DefaultFailureAction, DefaultSuccessAction } from "../common/reducers/utilities";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = note => note.internalid;
const prepareArray = notes => ensureArray(notes).map(note => ({ ...note, autogenerated: !!note.type }));

const eventNotesSlice = createSlice({
    name: 'eventNotes',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            const toSet = prepareArray(action.payload.data);
            const byEvent = groupBy(toSet, 'eventid');
            Object.entries(byEvent).forEach(([ eventid, notes ]) => {
                state.data[eventid] = notes;
            });
        },
        addOrUpdate: (state, action) => {
            const toUpdate = prepareArray(action.payload);
            const byEvent = groupBy(toUpdate, 'eventid');
            Object.entries(byEvent).forEach(([ eventid, notes ]) => {
                const ids = new Set(notes.map(n => key(n)));
                state.data[eventid] = (state.data[eventid]?.filter(n => !ids.has(key(n))) ?? []).concat(notes);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byEvent = groupBy(toRemove, 'eventid');
            Object.entries(byEvent).forEach(([eventid, notes]) => {
                if (state.data[eventid]) {
                    const toRemove = new Set(notes.map(n => key(n)));
                    state.data[eventid] = state.data[eventid].filter(n => !toRemove.has(key(n)));
                    if (state.data[eventid].length === 0) {
                        delete state.data[eventid]
                    }
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => eventNotesSlice.getInitialState());
    }
});


// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'eventNotes', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'eventNotes');

// TODO add multi-fetch
const fetchEventNotes = eventid => network => network.get('/api/v1/eventnote/' + encodeURIComponent(eventid), data => eventNotesSlice.actions.set({data, ids: [eventid]}), visualizeFailure('get'));
export const addEventNotes = (network, data) => network.post('/api/v1/eventnote', data, eventNotesSlice.actions.addOrUpdate, visualizeFailure('create'));
// #endregion

// #region selectors
const selectAllEventNoteData = state => state.eventNotes.data;
const selectEventNotes = eventid => createSelector([selectAllEventNoteData], notes => notes[eventid] ?? []);
// #endregion

const listen = {
    eventNoteCreated: (note, { dispatch, websocket }) => {
        markFetched(websocket, 'event_note', key(note))
        dispatch(eventNotesSlice.actions.addOrUpdate(note))
    }
}

const eventInitNotesByID = initMapFactory(fetchEventNotes, listen)
registerEntityBasedFetch('event', eventInitNotesByID)

const cleanup = { root: eventNotesSlice.name, all: state => Object.values(selectAllEventNoteData(state)).flat(), removeAll: eventNotesSlice.actions.remove, key };
export const [useEventNotes] = dataHookFactory(selectEventNotes, eventInitNotesByID, {cleanup});

const fullAccessRoles = ['administrator', 'event manager', 'template manager'];
const eventNotesReadRoles = ['auditor', 'event auditor', 'rostering manager', 'rostering auditor', 'recurring rostering manager', 'recurring rostering auditor', 'equipment manager', 'equipment auditor', 'EPCR manager', 'EPCR auditor', 'template auditor'];

const checkPermissions = (credentials, event, requiredRoles, condition = true) => {
    return (
        credentials.hasRole(requiredRoles, { office: event?.office, client: event?.client, location: event?.locationid }) &&
        condition
    );
};

export const canViewEventNotes = (credentials, event) => checkPermissions(credentials, event, eventNotesReadRoles);
export const canCreateEventNote = (credentials, event) => checkPermissions(credentials, event, fullAccessRoles);

const reducer = { name: eventNotesSlice.name, reducer: eventNotesSlice.reducer }
export default reducer;